import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';
import toast from 'react-hot-toast';

export default function Login() {
  const { login } = useAuth();
  const { fetchProgress } = useProgress();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return');

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    // ✅ Pass the 2FA token if required
    const success = await login(email, password, twoFactorToken);

    if (success) {
      await fetchProgress();
      if (returnUrl) {
        // Redirect back to invitation page
        window.location.href = returnUrl;
      } else {
        navigate('/');
      }
    } else {
      setError('Login failed. Please try again.');
    }
  } catch (err) {
    console.error('Login error:', err);

    // ✅ Check if 2FA is required
    if (err.response?.data?.requiresTwoFactor) {
      setRequiresTwoFactor(true);
      setError('Please enter your 2FA code');
    } else {
      setError('Network error. Please try again.');
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold mb-2">Login to Our Pickup</h1>
        <p className="text-gray-600 mb-6">Welcome back! Login to your account.</p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          {requiresTwoFactor && (
            <div>
              <label className="block text-gray-700 mb-2">2FA Code</label>
              <input
                type="text"
                value={twoFactorToken}
                onChange={(e) => setTwoFactorToken(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                placeholder="Enter 2FA code"
                required
              />
              <p className="text-xs text-gray-400 mt-1">Enter the 6-digit code from your authenticator app</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-4 text-center space-y-2">
          <Link to="/register" className="text-blue-500 hover:text-blue-700 text-sm block">
            Don't have an account? Register
          </Link>
          <Link to="/forgot-password" className="text-gray-500 hover:text-gray-700 text-sm block">
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
